import { setRecoil } from 'recoil-nexus'
import { userAtom } from '../../../recoil/atoms'
import urls, { CARRIER_HOME_PAGE, CARRIER_URLS } from '../../../routers/urls'
import { LoginHelper, MixPanel, UrlHelper } from '..'

export interface MakeRedirectionPageProps {
  authCountryHandler?: () => void
  authPolicyHandler?: () => void
  authUserNameHandler?: () => void
  authCompanyTitleHandler?: () => void
  data: {
    data: {
      id: string
      name: string
      familyName: string
      fullName: string
      tenantId: string
      language: string
      phoneNumber: string
      role: string
      tenant: {
        onboarding: {
          country: string
        }
        invoice: {
          companyTitle: string
        }
      }
      userPolicyAccepted: boolean
    }
  }
  error: boolean
  handler: (arg: string) => void
  isValidating?: boolean
  pathname: string
  search: string
  redirectToHome?: boolean
}

export const makeRedirectionPage = ({
  authCountryHandler = () => null,
  authPolicyHandler = () => null,
  authUserNameHandler = () => null,
  authCompanyTitleHandler = () => null,
  data,
  error,
  handler,
  isValidating = false,
  pathname = '',
  search = '',
  redirectToHome = false,
}: MakeRedirectionPageProps) => {
  if (isValidating) {
    return
  }

  const currentLocation = {
    isAuth: [urls.EmailLogin, urls.PhoneNumberLogin].includes(pathname),
    isCarrier: Object.values(CARRIER_URLS)
      .map(item => UrlHelper.getFirstPathItem(item))
      .includes(UrlHelper.getFirstPathItem(pathname)),
    isOtp: [urls.Otp].includes(pathname),
  }
  const hasCountry = !!data?.data?.tenant?.onboarding?.country
  const userPolicyAccepted = !!data?.data?.userPolicyAccepted
  const hasFullName = !!data?.data?.fullName
  const hasCompanyTitle = !!data?.data?.tenant?.invoice?.companyTitle
  setRecoil(userAtom, prev => {
    return {
      ...prev,
      role: data?.data?.role,
    }
  })

  // if the user already in auth-page, not re-direct
  if (error && !currentLocation.isAuth) {
    return {
      action: () => {
        const { phoneNumber = '', tripLegId = '' } = LoginHelper.getTripLegUrlDetails(pathname, search)

        return handler?.(
          `${pathname === urls.EmailLogin ? urls.EmailLogin : urls.PhoneNumberLogin}${
            phoneNumber && tripLegId
              ? `?phoneNumber=${encodeURIComponent(phoneNumber)}&tripLegId=${tripLegId}`
              : ''
          }`
        )
      },
      props: null,
    }
  }

  // set user information in mixpanel
  data?.data &&
    MixPanel.initializeUser({
      id: data?.data?.id,
      name: data?.data?.name,
      familyName: data?.data?.familyName,
      phoneNumber: data?.data?.phoneNumber,
      tenantId: data?.data?.tenantId,
      language: data?.data?.language,
    })

  // if the user on the private pages
  if ((!hasCountry || !userPolicyAccepted || !hasCountry || !hasFullName) && !currentLocation.isAuth) {
    return {
      action: () => handler?.(urls.PhoneNumberLogin),
    }
  }

  // if the user on the auth-page
  if (!hasCountry) {
    return {
      action: () => authCountryHandler(),
    }
  }

  if (!userPolicyAccepted) {
    return {
      action: () => authPolicyHandler(),
    }
  }

  if (!hasFullName) {
    return {
      action: () => authUserNameHandler(),
    }
  }

  if (!hasCompanyTitle) {
    return {
      action: () => authCompanyTitleHandler(),
    }
  }

  if (!currentLocation.isCarrier) {
    const { tripLegId = '', id = '', type = '' } = LoginHelper.getAuthUrlDetails(search)

    const makePath = () => {
      if (tripLegId) {
        return urls.TripLeg.replace(':id', tripLegId)
      }

      if (type === 'loads' && id) {
        return urls.Load.replace(':id', id)
      }

      return CARRIER_HOME_PAGE
    }

    return {
      action: () => handler?.(makePath()),
    }
  }

  if (redirectToHome) {
    return {
      action: () => handler?.(CARRIER_HOME_PAGE),
      props: {
        pathname: CARRIER_HOME_PAGE,
      },
    }
  }

  return {
    action: () => null,
  }
}

export const getOnNextPath = (currentPath: string): string => {
  const obj = [
    {
      path: [urls.PhoneNumberLogin],
      nextPath: urls.Otp,
    },
    {
      path: [urls.EmailLogin],
      nextPath: urls.EmailPassword,
    },
    // {
    //   path: [urls.Otp],
    //   nextPath: urls.CountrySelection,
    // },
    // {
    //   path: [urls.CountrySelection],
    //   nextPath: urls.Policies,
    // },
    {
      path: [urls.Policies],
      nextPath: urls.CompanyInfo,
    },
    {
      path: [urls.CompanyInfo],
      nextPath: urls.UserName,
    },
    {
      path: [urls.UserName],
      nextPath: CARRIER_HOME_PAGE,
    },
  ]

  return obj.find(item => item.path.includes(currentPath))?.nextPath || ''
}
